import React from 'react';
import ReactDOM from 'react-dom/client';
import './index.css';
import App from './App';
import reportWebVitals from './reportWebVitals';

// App version
export const APP_VERSION = '1.0.99';

// Show app info based on environment
const appEnv = process.env.REACT_APP_ENV || 'development';

if (appEnv === 'production') {
  // Production: minimal logging (only app name and version)
  console.log(`batchfather v${APP_VERSION}`);

  // Suppress all console methods except error in production
  const noop = () => {};
  console.log = noop;
  console.debug = noop;
  console.info = noop;
  console.warn = noop;
  // Keep console.error for production error tracking
} else {
  // Development & Staging: show detailed info
  const BUILD_TIME = new Date().toISOString();
  console.log(`%c Batchfather v${APP_VERSION} `, `background: ${appEnv === 'staging' ? '#f59e0b' : '#2563eb'}; color: white; font-weight: bold; padding: 4px 8px; border-radius: 4px;`);
  console.log(`Build time: ${BUILD_TIME}`);
  console.log(`Environment: ${appEnv} (NODE_ENV: ${process.env.NODE_ENV})`);

  // Suppress JSX transform warning from outdated dependencies
  const originalWarn = console.warn;
  console.warn = (...args) => {
    if (typeof args[0] === 'string' && args[0].includes('outdated JSX transform')) {
      return;
    }
    originalWarn.apply(console, args);
  };
}

// Global error handler for uncaught promise rejections
window.addEventListener('unhandledrejection', (event) => {
  // Suppress App Check and reCAPTCHA-related errors from appearing as uncaught
  if (event.reason?.message?.includes('appCheck') ||
      event.reason?.message?.includes('App Check') ||
      event.reason?.code?.includes('appCheck') ||
      event.reason?.message?.includes('recaptcha') ||
      event.reason?.message?.includes('reCAPTCHA') ||
      event.reason === null) {  // reCAPTCHA often rejects with null
    console.error('[App Check/reCAPTCHA] Promise rejection:', event.reason || 'null rejection (likely reCAPTCHA verification failure)');
    event.preventDefault(); // Prevent "Uncaught (in promise)" error
    return;
  }

  // Log other unhandled rejections
  console.error('[Unhandled Promise Rejection]', event.reason, {
    promise: event.promise,
    stack: event.reason?.stack
  });

  // Report to telemetry (lazy import keeps Firebase out of the critical path)
  import('./utils/telemetry').then(({ reportClientError }) => {
    reportClientError(event.reason, { source: 'unhandledrejection' });
  }).catch(() => {});
});

// Global handler for uncaught synchronous errors
window.addEventListener('error', (event) => {
  import('./utils/telemetry').then(({ reportClientError }) => {
    reportClientError(event.error || event.message, { source: 'window.onerror' });
  }).catch(() => {});
});

const root = ReactDOM.createRoot(
  document.getElementById('root') as HTMLElement
);
root.render(
  <React.StrictMode>
    <App />
  </React.StrictMode>
);

// Send Core Web Vitals to Firebase Analytics so field performance is measurable
reportWebVitals((metric) => {
  import('./firebase').then(async ({ analytics }) => {
    const instance = await analytics;
    if (!instance) return;
    const { logEvent } = await import('firebase/analytics');
    logEvent(instance, 'web_vitals', {
      metric_name: metric.name,
      metric_value: Math.round(metric.name === 'CLS' ? metric.value * 1000 : metric.value),
      metric_id: metric.id,
    });
  }).catch(() => {});
});
